import { BodySecondary } from '@liveauctioneers/hammer-ui-core/text';
import { FormattedMessage } from '@liveauctioneers/hammer-ui-core/intl';
import { getItemSummary } from '@/redux/modules/item/summary/itemSummary.selectors';
import { getLiveItemStatusOpen } from '@/redux/modules/liveItemStatus';
import { isMyBidsPage, isSavedItemsPage } from '@/utils/urls';
import { isUserLoggedIn } from '@/redux/modules/account/user/user.selectors';
import { ItemCardComponent, ItemCardInfo } from '@/components/GridItemCard/types/types';
import { ItemCardSource, useTrackItemCardClicked } from '@/utils/amplitude/analytics';
import { useAppSelector } from '@/redux/hooks';
import { useLocation } from 'react-router-dom';
import ItemCardBadge from '@/components/GridItemCard/Card/components/ItemCardBadge/ItemCardBadge';
import ItemCardImage from '@/components/GridItemCard/Card/components/ItemCardImage/ItemCardImage';
import ItemCardMenu from '@/components/ItemCard/components/Menu/ItemCardMenu';
import ItemCardPriceRow from '@/components/GridItemCard/Card/components/ItemCardPriceRow/ItemCardPriceRow';
import ItemCardSaveIcon from '@/components/GridItemCard/Card/components/ItemCardSaveIcon/ItemCardSaveIcon';
import ItemCardShortDate from '@/components/GridItemCard/Card/components/ItemCardShortDate/ItemCardShortDate';
import ItemCardTitle from '@/components/GridItemCard/Card/components/ItemCardTitle/ItemCardTitle';
import ms from 'ms';
import React, { useCallback, useState } from 'react';
import styled from 'styled-components';
import theme from '@liveauctioneers/caterwaul-components/lib/theme/theme';
import useIsWiderThan from '@/hooks/useIsWiderThan';

const PastItemCardSmall: ItemCardComponent = ({ className, itemId }) => {
    const trackItemCardClicked = useTrackItemCardClicked();
    const { pathname } = useLocation();

    const isLoggedIn = useAppSelector(isUserLoggedIn);
    const isLotOpen = useAppSelector((state) => getLiveItemStatusOpen(state, itemId));

    const { buyNowStatus, catalogStatus, isLocked, isPassed, isSold, saleStartTs } = useAppSelector((state) =>
        getItemSummary(state, itemId)
    );
    const [hover, setHover] = useState(false);
    const [linkHover, setLinkHover] = useState(false);
    const isMobile = !useIsWiderThan(theme.breakpoints.tabletNarrowWidthNum);
    const biddingWithin7Days = Date.now() > saleStartTs * 1000 - ms('7d');
    const showDateWithoutYear = catalogStatus === 'online' && !biddingWithin7Days;

    const isBuyNow = buyNowStatus === 1;

    const onCardClick = useCallback(() => {
        if (isSavedItemsPage(pathname)) {
            trackItemCardClicked({ itemId, source: ItemCardSource.MyItemsPageFavoritesPast });
        } else if (isMyBidsPage(pathname)) {
            trackItemCardClicked({ itemId, source: ItemCardSource.MyItemsPageBidsPast });
        }
    }, [pathname, itemId, trackItemCardClicked]);

    const enterHoverState = React.useCallback(() => {
        setHover(true);
    }, []);

    const leaveHoverState = React.useCallback(() => {
        setHover(false);
    }, []);

    const enterLinkHoverState = React.useCallback(() => {
        setLinkHover(true);
    }, []);

    const leaveLinkHoverState = React.useCallback(() => {
        setLinkHover(false);
    }, []);

    const isPending = isLocked && !isPassed && !isSold && !isLotOpen;
    const auctionDateInfo = isBuyNow ? (
        <BodySecondary color="secondary">
            <FormattedMessage id="buy_now" />
        </BodySecondary>
    ) : (
        <ItemCardShortDate
            allowBidding
            itemId={itemId}
            showDateWithoutYear={showDateWithoutYear}
            showOnlyYear={isSold || isPassed || isPending}
        />
    );
    return (
        <StyledCard
            data-testid={`saved-item-card-${itemId}`}
            onClick={onCardClick}
            onMouseEnter={enterHoverState}
            onMouseLeave={leaveHoverState}
        >
            <StyledExpansionContainer data-testid="expansion-container">
                <SavedItemsCardGrid
                    className={className}
                    data-testid="saved-items-card-grid"
                >
                    <StyledImageContainer
                        data-testid="image-container"
                        onMouseEnter={enterLinkHoverState}
                        onMouseLeave={leaveLinkHoverState}
                    >
                        {!isMobile && (
                            <ItemCardBadge
                                itemId={itemId}
                                savedItemsCard
                            />
                        )}
                        <StyledFavoriteButton>
                            <ItemCardSaveIcon itemId={itemId} />
                        </StyledFavoriteButton>
                        <PlacedItemCardImage
                            itemId={itemId}
                            shouldGiveRangeForMobileSize={isMobile}
                            showBidStatus
                            useInFluidGrid
                        />
                    </StyledImageContainer>
                    <PlacedDetailsForNewMobileGrid>
                        <PlacedItemInfo>
                            <StyledDateInfo>{auctionDateInfo}</StyledDateInfo>
                        </PlacedItemInfo>
                        <PlacedItemCardTitle
                            isHovered={linkHover}
                            itemId={itemId}
                            onMouseEnter={enterLinkHoverState}
                            onMouseLeave={leaveLinkHoverState}
                            showLotNumber
                            testid="itemCardTitle"
                        />
                        <PlacedPriceInformation>
                            <StyledBidInfo>
                                <ItemCardPriceRow
                                    isHovered={linkHover}
                                    itemId={itemId}
                                    onMouseEnter={enterLinkHoverState}
                                    onMouseLeave={leaveLinkHoverState}
                                />
                            </StyledBidInfo>
                            {isLoggedIn && (
                                <ItemCardMenu
                                    itemId={itemId}
                                    showIcon={hover}
                                />
                            )}
                        </PlacedPriceInformation>
                    </PlacedDetailsForNewMobileGrid>
                </SavedItemsCardGrid>
            </StyledExpansionContainer>
        </StyledCard>
    );
};

const info: ItemCardInfo = {
    ItemCardComponent: PastItemCardSmall,
};

export default info;

const StyledCard = styled.article`
    position: relative;
`;

const StyledExpansionContainer = styled.section`
    margin: 0 auto;
`;

const PlacedItemCardImage = styled(ItemCardImage)`
    grid-area: itemimage;
`;

const PlacedDetailsForNewMobileGrid = styled.div`
    display: grid;
    overflow: hidden;
    grid-area: alldetailsformobilelayout;
    grid-template-areas: '.' 'iteminfo' 'title' '.' 'currentbidprice';
    grid-template-rows: 6px 22px min-content 4px;

    @media (max-width: ${theme.breakpoints.tabletNarrowWidth}) {
        grid-template-rows: 24px min-content 4px;
        grid-template-areas: 'iteminfo' 'title' '.' 'currentbidprice';
    }
`;

const StyledFavoriteButton = styled.span`
    position: absolute;
    top: 8px;
    right: 8px;
    z-index: 2;
    @media (max-width: ${theme.breakpoints.tabletNarrowWidth}) {
        top: 4px;
        right: 5px;
    }
`;

const PlacedItemInfo = styled.div`
    grid-area: iteminfo;
    display: flex;
    justify-content: space-between;
    min-width: 0;
`;

const StyledDateInfo = styled.span`
    width: 100%;
    line-height: 18px;
`;

const PlacedItemCardTitle = styled(ItemCardTitle)`
    grid-area: title;
`;

const StyledBidInfo = styled.div`
    display: flex;
    align-items: center;
`;

const PlacedPriceInformation = styled.div`
    grid-area: currentbidprice;
    display: flex;
    justify-content: space-between;
`;

const StyledImageContainer = styled.div`
    position: relative;
    aspect-ratio: 1/1;
`;

const SavedItemsCardGrid = styled.section`
    display: grid;
    width: inherit;
    grid-template-columns: 1fr;
    grid-template-rows: min-content min-content min-content;
    grid-template-areas:
        'itemimage'
        '.'
        'alldetailsformobilelayout';
    justify-content: center;

    @media (max-width: ${theme.breakpoints.tabletNarrowWidth}) {
        grid-template-columns: 1fr 4fr;
        grid-template-areas: 'itemimage alldetailsformobilelayout';
        column-gap: 16px;
    }
    @media (max-width: 375px) {
        grid-template-areas: 'itemimage alldetailsformobilelayout';
        grid-template-rows: 116px;
    }
`;
